import type { ClientDirectory, ClientWithIntegrations, CreateClientInput } from '../../clients/model.js';

export class ClientRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ClientRequestError';
  }
}

export class LocalClientProvider {
  async load(identityToken: string): Promise<ClientDirectory> {
    return await request('/api/clients', 'GET', identityToken) as ClientDirectory;
  }

  async loadDetail(identityToken: string, clientId: string): Promise<ClientWithIntegrations> {
    return await request(`/api/clients/${encodeURIComponent(clientId)}`, 'GET', identityToken) as ClientWithIntegrations;
  }

  async create(identityToken: string, input: CreateClientInput): Promise<ClientWithIntegrations> {
    return await request('/api/clients', 'POST', identityToken, input) as ClientWithIntegrations;
  }

  async createFromNinja(identityToken: string, organizationId: number): Promise<ClientWithIntegrations> {
    return await request('/api/clients/from-ninja', 'POST', identityToken, { organizationId }) as ClientWithIntegrations;
  }

  async linkNinja(
    identityToken: string,
    clientId: string,
    organizationId: number,
  ): Promise<ClientWithIntegrations> {
    return await request(
      `/api/clients/${encodeURIComponent(clientId)}/ninja-organizations`,
      'POST', identityToken, { organizationId },
    ) as ClientWithIntegrations;
  }
}

async function request(
  path: string,
  method: 'GET' | 'POST',
  identityToken: string,
  body?: unknown,
): Promise<unknown> {
  if (identityToken.length === 0) throw new ClientRequestError('Authenticated session is unavailable');
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers: {
        Authorization: `Bearer ${identityToken}`,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      cache: 'no-store',
    });
  } catch {
    throw new ClientRequestError('Client service is unavailable');
  }
  const payload = await response.json().catch(() => null) as unknown;
  if (!response.ok) {
    const message = typeof payload === 'object' && payload !== null
      && 'error' in payload && typeof payload.error === 'string'
      ? payload.error : 'Client request could not be completed';
    throw new ClientRequestError(message);
  }
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
    throw new ClientRequestError('Client service returned an invalid response');
  }
  return payload;
}
